// Pure, framework-free logic for the Manage Subscription screen. Mirrors the
// iOS `ManageSubscriptionStateMapper` / Android `ManageSubscriptionStateMapper`
// so web renders the same product names, pill states, dates and payloads.
// Everything here is deterministic and unit-tested in
// __tests__/subscriptionLogic.test.ts.

export type PillState = "active" | "cancelled" | "paused" | "payment_failed";
export type SubscriptionSource = "stripe" | "appstore" | "playstore" | "other";
export type PauseDurationEnum = "ONE_MONTH" | "THREE_MONTHS" | "SIX_MONTHS";

export type CancelReasonSlug =
  | "too_expensive"
  | "dont_use_enough"
  | "switching_apps"
  | "missing_features"
  | "not_working"
  | "other";

// Every promotional grant — 5 year offer, 3 year offer, gift card, ambassador,
// group seat, Hillwalk tour — arrives as the SAME product identifier
// (`rc_promo_PRO +_custom`), so the offer it came from is genuinely not on the
// wire. Naming it from the grant's own length would be a guess and usually a
// wrong one: grant_five_year adds the remaining time of the subscription it
// replaces, so Jack's "5 year" purchase spans 5 years 11 months. Say what we
// know instead of inventing a duration.
export function isFixedTerm(store: string | null | undefined): boolean {
  return (store || "").toLowerCase() === "promotional";
}

export function productName(
  productIdentifier: string | null | undefined,
  store?: string | null
): string {
  if (isFixedTerm(store)) return "PRO+ access";
  const id = (productIdentifier || "").toLowerCase();
  if (id.includes("annual") || id.includes("yearly") || id.includes("year")) {
    return "PRO+ Annual subscription";
  }
  if (id.includes("monthly") || id.includes("month")) {
    return "PRO+ Monthly subscription";
  }
  if (id.includes("lifetime")) {
    return "PRO+ Lifetime subscription";
  }
  return "PRO+ subscription";
}

export function pillState(
  entitlement: {
    willRenew?: boolean | null;
    expirationDate?: string | Date | null;
    store?: string | null;
  },
  isPaused = false,
  paymentFailed = false
): PillState {
  // Payment failure outranks everything else: it's the only state the customer
  // has to act on, and it's how a pause restart that couldn't charge surfaces.
  if (paymentFailed) return "payment_failed";
  if (isPaused) return "paused";
  // Lifetime (no expiry) and auto-renewing subs are "active".
  if (!entitlement.expirationDate) return "active";
  // A fixed-term grant never renews, so willRenew is always false on it — but
  // nobody cancelled anything, and while the grant runs the access is simply
  // active. Reading willRenew literally told ~2,400 people who had paid for
  // multi-year access that they had none; a "Fixed term" pill of its own then
  // described the shape of the purchase in place of the status they came to
  // read. An expired grant isn't in `entitlements.active`, so a rendered one is
  // by definition still running. The grant still drives the date row ("Access
  // until") and hides the billing rows — see renewsOnLabel and
  // hidesBillingActions — but the pill reports status, and the status is active.
  if (isFixedTerm(entitlement.store)) return "active";
  return entitlement.willRenew ? "active" : "cancelled";
}

// `isFixedTerm` is passed separately from the pill state because a running
// grant now reports its status as "active": the date row still has to read
// "Access until" rather than "Renews on", since a grant has no renewal.
export function renewsOnLabel(state: PillState, isFixedTerm = false): string {
  switch (state) {
    case "cancelled":
      return "Ends on";
    case "paused":
      return "Resumes on";
    case "payment_failed":
      return "Payment due";
    default:
      return isFixedTerm ? "Access until" : "Renews on";
  }
}

export const PAYMENT_FAILED_MESSAGE =
  "We couldn't take payment for your subscription. Update your payment method to restore PRO+.";

export function subscriptionSource(store: string | null | undefined): SubscriptionSource {
  switch ((store || "").toLowerCase()) {
    case "stripe":
      return "stripe";
    case "app_store":
    case "appstore":
    case "mac_app_store":
      return "appstore";
    case "play_store":
    case "playstore":
      return "playstore";
    default:
      return "other";
  }
}

// Pause is only wired end-to-end against the Stripe backend mutation — neither
// App Store nor Play Store exposes a pause primitive we can drive from the web.
// Only an *active* sub can be paused: a paused one has nothing to pause, and a
// cancelled-but-not-yet-expired one would pause past its own end date (Stripe
// happily accepts pause_collection on a cancel_at_period_end sub).
export function hidesPauseAction(source: SubscriptionSource, state: PillState): boolean {
  return source !== "stripe" || state !== "active";
}

// A fixed-term grant has no billing relationship at all: no store subscription
// to cancel, no card on file to update. Both rows used to be shown regardless,
// and each could only ever dead-end at a support hand-off for something that
// needs no action. Keyed off the grant itself rather than the pill state, which
// now reads "active" for a running grant like any other live access.
export function hidesBillingActions(isFixedTerm: boolean): boolean {
  return isFixedTerm;
}

// Resume is pause's mirror: only a paused Stripe subscription can be resumed,
// and no subscription is ever both pausable and resumable.
export function hidesResumeAction(source: SubscriptionSource, state: PillState): boolean {
  return source !== "stripe" || state !== "paused";
}

// An already-cancelled subscription has nothing left to cancel: it is running out
// the period it has been paid for, and there is no un-cancel mutation to reverse
// that either. The row was gated on the fixed-term grant check alone, so finishing
// the cancel flow returned to a Manage screen still offering "Cancel my
// subscription" — and a second pass would re-run CancelWebSubscription against a
// subscription that is already cancelled.
//
// A PAUSED sub keeps the row on purpose: it still has a live billing relationship
// and a real subscription behind it, and Stripe accepts cancelling one, so a paused
// customer who wants out must not have to resume first.
//
// The fixed-term check is folded in so this row, like the pause and resume rows,
// answers to a single flag (hidesBillingActions still gates the payment row).
export function hidesCancelAction(state: PillState, isFixedTerm: boolean): boolean {
  return isFixedTerm || state === "cancelled";
}

// Shown in place of the renewal row when Stripe couldn't collect, so the
// customer knows why PRO+ stopped and what to do about it.
export function showsPaymentFailedNotice(state: PillState): boolean {
  return state === "payment_failed";
}

export type CancelStep = "store" | "lose" | "renewal" | "reason" | "pause" | "final";

// The cancel flow's step sequence per billing source. Stripe subs get the full
// retention flow ending in a real cancellation. Store-billed subs (and sources
// with no self-serve page at all) can't be cancelled by HiiKER, so they're told
// that on the FIRST screen — the store hand-off — and the retention content
// after it is optional. No "what you'll lose" and no final confirmation for
// them: we write nothing, so a red confirm button would only obstruct.
//
// The in-flow pause offer follows the Manage screen's pause row on purpose:
// offering "pause instead" to a subscription that can't be paused — an
// already-paused one above all — submits a mutation that can only fail.
export function cancelFlowSteps(source: SubscriptionSource, state: PillState = "active"): CancelStep[] {
  if (source !== "stripe") return ["store", "renewal", "reason"];
  if (hidesPauseAction(source, state)) return ["lose", "renewal", "reason", "final"];
  return ["lose", "renewal", "reason", "pause", "final"];
}

export const APP_STORE_SUBSCRIPTIONS_URL = "https://apps.apple.com/account/subscriptions";
export const PLAY_STORE_SUBSCRIPTIONS_URL = "https://play.google.com/store/account/subscriptions";

// Where "Go" sends a store-billed subscriber to manage payment/subscription.
// Prefers RevenueCat's managementURL (CustomerInfo exposes a single nullable
// URL, not one per store) and falls back to the well-known store pages.
export function storeManagementUrl(
  source: SubscriptionSource,
  managementURL: string | null | undefined
): string | null {
  switch (source) {
    case "appstore":
      return managementURL || APP_STORE_SUBSCRIPTIONS_URL;
    case "playstore":
      return managementURL || PLAY_STORE_SUBSCRIPTIONS_URL;
    default:
      return null;
  }
}

export function storeLabelFor(source: SubscriptionSource): string {
  switch (source) {
    case "appstore":
      return "App Store";
    case "playstore":
      return "Play Store";
    default:
      return "store";
  }
}

// How to route "update payment" / "cancel" for a given source. `other` covers
// RevenueCat sources we can't deep-link (promotional grants, Amazon, …): those
// have no self-serve page at all, so they get a support hand-off rather than a
// store redirect whose button would have nothing to open.
export type ManageAction =
  | { kind: "stripe" }
  | { kind: "store"; url: string; storeLabel: string }
  | { kind: "support" };

export function manageAction(
  source: SubscriptionSource,
  managementURL: string | null | undefined
): ManageAction {
  if (source === "stripe") return { kind: "stripe" };
  const url = storeManagementUrl(source, managementURL);
  return url ? { kind: "store", url, storeLabel: storeLabelFor(source) } : { kind: "support" };
}

export function updatePaymentPrompt(action: ManageAction): string {
  return action.kind === "store"
    ? `To update your payment details go to the ${action.storeLabel}`
    : "This subscription wasn't purchased through the web, App Store or Play Store. Contact support and we'll sort it out.";
}

// Copy for the store hand-off screen that opens a non-Stripe cancel flow. The
// CTA is named per the actual store ("Go to Play Store"), never generically.
export interface StoreCancelCopy {
  title: string;
  body: string;
  cta: string;
}

export function storeCancelCopy(action: ManageAction): StoreCancelCopy {
  if (action.kind === "store") {
    return {
      title: `Cancel through the ${action.storeLabel}`,
      body:
        `Your subscription was purchased through the ${action.storeLabel}, so HiiKER can't cancel it ` +
        `for you — it has to be cancelled in the ${action.storeLabel} directly. We'll take you there. ` +
        `Your PRO+ access continues until the end of the current billing period.`,
      cta: `Go to ${action.storeLabel}`,
    };
  }
  return {
    title: "We'll cancel it for you",
    body:
      "This subscription wasn't purchased through the web, App Store or Play Store. " +
      "Contact support and we'll cancel it for you.",
    cta: "Contact support",
  };
}

const ORDINAL_SUFFIX = (day: number): string => {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// "July 26th 2025"
export function longOrdinalDate(value: string | Date | null | undefined): string | null {
  const date = toDate(value);
  if (!date) return null;
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const day = date.getDate();
  return `${month} ${day}${ORDINAL_SUFFIX(day)} ${date.getFullYear()}`;
}

// "Aug 13, 2026"
export function shortDate(value: string | Date | null | undefined): string | null {
  const date = toDate(value);
  if (!date) return null;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Reminder shown one week before renewal (expiry − 7 days).
export function reminderDate(expiry: string | Date | null | undefined): Date | null {
  const date = toDate(expiry);
  if (!date) return null;
  const result = new Date(date.getTime());
  result.setDate(result.getDate() - 7);
  return result;
}

// A pause runs from the end of the period already paid for, not from today, so
// the client cannot work out the resume date — only the backend knows when the
// current period ends. The picker therefore describes the pause by its length
// and the confirmation uses the dates the mutation returns.
export function pauseDurationLabel(months: number): string {
  return months === 1 ? "1 month" : `${months} months`;
}

// Lead-in for the pause confirmation, which ends with the (bolded) new renewal
// date. Both dates come from the mutation; when the backend couldn't report the
// access-ends date we simply don't claim one.
export function pauseConfirmationPrefix(accessEndsOn: string | null | undefined): string {
  return accessEndsOn
    ? `You keep PRO+ until ${accessEndsOn}. Billing then pauses, and your subscription renews on `
    : "Your subscription is paused. Billing resumes on ";
}

// A pause has two halves and resuming means something different in each, so the
// customer has to be told which one they're in before they confirm. While the
// already-paid period is still running the subscription is alive: resuming
// un-cancels it and charges nothing. Once access has ended there is no
// subscription left to un-cancel, so resuming starts a fresh one and bills the
// saved card immediately. `pauseAccessEndsAt` is the boundary — the backend
// branches on the same date in `Subscriptions::StripePause.resumable_in_place?`.
export function resumeChargesImmediately(
  pauseAccessEndsAt: string | Date | null | undefined,
  now: Date = new Date()
): boolean {
  // No date at all means we can't prove access is still running, so warn about
  // the charge rather than promising a free resume we can't guarantee.
  return !toDate(pauseAccessEndsAt) || accessHasEnded(pauseAccessEndsAt, now);
}

// Distinct from the above on purpose: an absent date is a reason to warn about a
// charge, but never a reason to tell the customer their access has ended.
export function accessHasEnded(
  pauseAccessEndsAt: string | Date | null | undefined,
  now: Date = new Date()
): boolean {
  const accessEnds = toDate(pauseAccessEndsAt);
  return accessEnds !== null && accessEnds.getTime() <= now.getTime();
}

export function resumeExplanation(
  chargesImmediately: boolean,
  accessEndsOn: string | null | undefined
): string {
  if (chargesImmediately) {
    return "Your PRO+ access has already ended, so resuming starts your subscription again now and charges your saved payment method.";
  }
  return accessEndsOn
    ? `You won't be charged anything now — your subscription carries on as normal and renews on ${accessEndsOn}.`
    : "You won't be charged anything now — your subscription carries on as normal.";
}

export const RESUME_CONFIRMED_TITLE = "Welcome back!";
// Deliberately date-free: the mutation reports only success, and which renewal
// date the customer lands on depends on the branch the backend took. The screen
// behind this reloads from the backend and shows the real date.
export const RESUME_CONFIRMED_BODY = "Your PRO+ subscription is active again.";

// Backend `reason` for CancelWebSubscription: the analytics slug, or for "other"
// with free text `other: <text>` (just `other` when the box is empty/absent).
export function cancelReasonPayload(
  slug: CancelReasonSlug | null,
  otherText = ""
): string | null {
  if (!slug) return null;
  if (slug === "other") {
    const trimmed = otherText.trim();
    return trimmed ? `other: ${trimmed}` : "other";
  }
  return slug;
}

export function pauseDurationEnum(months: number): PauseDurationEnum {
  switch (months) {
    case 3:
      return "THREE_MONTHS";
    case 6:
      return "SIX_MONTHS";
    default:
      return "ONE_MONTH";
  }
}

export interface Entitlement {
  productIdentifier?: string | null;
  store?: string | null;
  willRenew?: boolean | null;
  originalPurchaseDate?: string | Date | null;
  expirationDate?: string | Date | null;
}

export const ACCESS_ENDS_LABEL = "Access until";
export const ACCESS_ENDED_LABEL = "Access ended";

// Mid-gap the access date is in the past, and "Access until <last year>" reads
// like a mistake.
export function accessEndsLabel(hasEnded: boolean): string {
  return hasEnded ? ACCESS_ENDED_LABEL : ACCESS_ENDS_LABEL;
}

export interface DisplayModel {
  productName: string;
  pillState: PillState;
  // Dated access that was never a billing relationship — a promotional grant
  // (5 year offer, 3 year offer, gift card, ambassador award, group seat,
  // Hillwalk tour). Not a pill state: a running grant's pill reads "Active"
  // like any other live access. It is what makes the date row read "Access
  // until" and hides the billing rows.
  isFixedTerm: boolean;
  priceString: string;
  startedOn: string | null;
  renewsOn: string | null;
  renewsOnLabel: string;
  // Only set while paused: a pause revokes PRO+ when the period already paid
  // for runs out, which is a different date from when billing resumes.
  accessEndsOn: string | null;
  accessEndsOnLabel: string;
  paymentFailedMessage: string | null;
  source: SubscriptionSource;
  hidesPauseAction: boolean;
  hidesBillingActions: boolean;
  hidesResumeAction: boolean;
  hidesCancelAction: boolean;
  // Whether resuming would bill immediately, so the resume screen can say so
  // before the customer commits.
  resumeChargesImmediately: boolean;
  // Raw ISO expiry, so a card can drive its own cancel flow rather than the
  // page having to remember which entitlement a flow belongs to.
  expirationDate: string | null;
  // Set only on a still-renewing subscription that another entitlement already
  // outlasts — the double-billing warning. Null on everything else.
  renewalNudge: string | null;
  // Stable identity for list rendering and de-duplication.
  key: string;
}

export function buildDisplayModel(
  entitlement: Entitlement,
  opts: {
    isPaused?: boolean;
    pauseResumesAt?: string | Date | null;
    pauseAccessEndsAt?: string | Date | null;
    paymentFailed?: boolean;
    priceString?: string | null;
    renewalNudge?: string | null;
    now?: Date;
  } = {}
): DisplayModel {
  const isPaused = opts.isPaused ?? false;
  const source = subscriptionSource(entitlement.store);
  const state = pillState(entitlement, isPaused, opts.paymentFailed ?? false);
  const fixedTerm = isFixedTerm(entitlement.store);
  const now = opts.now ?? new Date();
  // When paused, prefer the backend's pauseResumesAt over the entitlement's
  // expirationDate: the entitlement still carries the *old* renewal date, while
  // pauseResumesAt is the new one (old renewal + the pause length). Fall back to
  // expiry if it's absent.
  const renewsSource =
    state === "paused" ? opts.pauseResumesAt ?? entitlement.expirationDate : entitlement.expirationDate;
  return {
    productName: productName(entitlement.productIdentifier, entitlement.store),
    pillState: state,
    isFixedTerm: fixedTerm,
    priceString: opts.priceString || "—",
    startedOn: longOrdinalDate(entitlement.originalPurchaseDate),
    renewsOn: longOrdinalDate(renewsSource),
    renewsOnLabel: renewsOnLabel(state, fixedTerm),
    accessEndsOn: state === "paused" ? longOrdinalDate(opts.pauseAccessEndsAt) : null,
    accessEndsOnLabel: accessEndsLabel(accessHasEnded(opts.pauseAccessEndsAt, now)),
    paymentFailedMessage: showsPaymentFailedNotice(state) ? PAYMENT_FAILED_MESSAGE : null,
    source,
    hidesPauseAction: hidesPauseAction(source, state),
    hidesBillingActions: hidesBillingActions(fixedTerm),
    hidesResumeAction: hidesResumeAction(source, state),
    hidesCancelAction: hidesCancelAction(state, fixedTerm),
    resumeChargesImmediately:
      state === "paused" && resumeChargesImmediately(opts.pauseAccessEndsAt, now),
    expirationDate: toIso(entitlement.expirationDate),
    renewalNudge: opts.renewalNudge ?? null,
    key: entitlementKey(entitlement),
  };
}

// RevenueCat only knows about entitlements, and a pause deliberately lets the
// subscription lapse — so once access ends there is no active entitlement, and
// the page would tell a paused customer they have no subscription at all, with
// no way back. Same for a restart that fired and couldn't charge. The backend
// still knows, from Stripe, that a pause or a failed payment is in flight, so
// build the card from that when RevenueCat has nothing.
//
// Returns null when there's genuinely nothing to show, i.e. a free user.
export function buildFallbackDisplayModel(
  pause: PauseState,
  opts: { priceString?: string | null; now?: Date } = {}
): DisplayModel | null {
  if (!pause.isPaused && !pause.paymentFailed) return null;

  const state: PillState = pause.paymentFailed ? "payment_failed" : "paused";
  const now = opts.now ?? new Date();
  // Both flags only ever come from the Stripe read path, so the source is known
  // even though there's no entitlement to read it off.
  const source: SubscriptionSource = "stripe";
  return {
    // No entitlement means no product identifier; the generic name is honest
    // rather than guessing between monthly and annual.
    productName: productName(null),
    pillState: state,
    // Both flags describe a Stripe subscription, so this card is never a grant.
    isFixedTerm: false,
    priceString: opts.priceString || "—",
    startedOn: null,
    renewsOn: longOrdinalDate(pause.pauseResumesAt),
    renewsOnLabel: renewsOnLabel(state),
    accessEndsOn: state === "paused" ? longOrdinalDate(pause.pauseAccessEndsAt) : null,
    accessEndsOnLabel: accessEndsLabel(accessHasEnded(pause.pauseAccessEndsAt, now)),
    paymentFailedMessage: showsPaymentFailedNotice(state) ? PAYMENT_FAILED_MESSAGE : null,
    source,
    hidesPauseAction: hidesPauseAction(source, state),
    hidesBillingActions: hidesBillingActions(false),
    hidesResumeAction: hidesResumeAction(source, state),
    hidesCancelAction: hidesCancelAction(state, false),
    resumeChargesImmediately:
      state === "paused" && resumeChargesImmediately(pause.pauseAccessEndsAt, now),
    // The paid period running out is the only expiry a paused sub has; there is
    // no entitlement to read one off.
    expirationDate: toIso(pause.pauseAccessEndsAt),
    // Nothing to compare against — the fallback exists precisely because
    // RevenueCat reported no entitlements at all.
    renewalNudge: null,
    key: "stripe|pause-fallback",
  };
}

// --- Multiple entitlements -------------------------------------------------
// The page used to render `Object.values(entitlements.active)[0]` — the first
// key RevenueCat happened to serialise. Holding two at once is normal here, not
// exotic: the legacy Android products `app.hiiker.pro.annual` / `.sale` are
// attached to BOTH the `PRO` and `PRO +` entitlements, and every multi-year
// offer, gift card, ambassador award, group seat and Hillwalk tour arrives as a
// separate promotional grant on `PRO +`. 3,997 accounts hold both today. For
// ~86 of them the arbitrary pick showed the wrong subscription entirely — a
// 5-year customer was shown the cancelled Play Store sub he'd just replaced,
// with his 5 years of access nowhere on the page.

// Two entitlements fed by the SAME purchase are one subscription, not two. This
// is the common case, not the rare one: for 3,927 of those 3,997 accounts `PRO`
// and `PRO +` are both granted by one store product, so without this the fix
// would replace a wrong card with a duplicated one.
export function entitlementKey(entitlement: Entitlement): string {
  return [
    (entitlement.store || "").toLowerCase(),
    entitlement.productIdentifier || "",
    toIso(entitlement.expirationDate) || "lifetime",
  ].join("|");
}

export function dedupeEntitlements(entitlements: Entitlement[]): Entitlement[] {
  const seen = new Set<string>();
  return entitlements.filter((entitlement) => {
    const key = entitlementKey(entitlement);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// How long this entitlement keeps the user in PRO+. Lifetime (no expiry) sorts
// above every dated one.
export function accessUntil(entitlement: Entitlement): number {
  const date = entitlement.expirationDate
    ? new Date(entitlement.expirationDate as string | Date)
    : null;
  if (!date) return Number.POSITIVE_INFINITY;
  const time = date.getTime();
  return Number.isNaN(time) ? 0 : time;
}

// Longest-lasting access leads, because that is the answer to "what have I
// got?" — the question this page exists to answer. Ordering deliberately does
// NOT promote whatever is billed: an earlier attempt at this fix ranked the
// billed subscription first, which for 67 accounts would have led with a
// monthly sub while burying the 5 years of access behind it. Anything needing
// action carries its own nudge instead, so nothing has to lead to be seen.
// Ties break towards the renewing subscription, so the card with live billing
// leads when the dates are level.
export function sortEntitlements(entitlements: Entitlement[]): Entitlement[] {
  return [...entitlements].sort((a, b) => {
    const diff = accessUntil(b) - accessUntil(a);
    if (diff !== 0) return diff;
    return Number(Boolean(b.willRenew)) - Number(Boolean(a.willRenew));
  });
}

// A renewal is redundant when the user is about to be charged again for time
// another entitlement already covers. That is precisely the state our own
// 5-year confirmation email asks people to fix ("please cancel your current
// Android Subscription so it does not renew") — and the page they'd go to do it
// was hiding the subscription in question. ~49 accounts are currently set to be
// charged for a renewal they don't need.
export function isRedundantRenewal(entitlement: Entitlement, others: Entitlement[]): boolean {
  if (!entitlement.willRenew) return false;
  if (isFixedTerm(entitlement.store)) return false;
  const until = accessUntil(entitlement);
  return others.some(
    (other) => entitlementKey(other) !== entitlementKey(entitlement) && accessUntil(other) > until
  );
}

// Names the store so the user knows where to go, and leads with the
// reassurance: cancelling changes nothing about their access. Stripe subs we
// can cancel on this page; store-billed ones only the user can.
export function redundantRenewalCopy(
  source: SubscriptionSource,
  chargedOn: string | null,
  coveredUntil: string | null
): string {
  const charge = chargedOn ? ` on ${chargedOn}` : "";
  const covered = coveredUntil ? ` until ${coveredUntil}` : "";
  const lead = `You already have PRO+ access${covered}, so this subscription is renewing for time you have.`;
  const tail = "Your PRO+ access won't change.";
  switch (source) {
    case "stripe":
      return `${lead} Cancel it below so you aren't charged again${charge}. ${tail}`;
    case "appstore":
    case "playstore":
      return `${lead} Cancel it in the ${storeLabelFor(source)} so you aren't charged again${charge}. ${tail}`;
    default:
      return `${lead} Contact support and we'll stop it renewing${charge}. ${tail}`;
  }
}

// The page's whole model: every active entitlement, de-duplicated, longest
// access first, each carrying its own actions and its own warning.
export function buildDisplayModels(
  entitlements: Entitlement[],
  opts: {
    isPaused?: boolean;
    pauseResumesAt?: string | Date | null;
    pauseAccessEndsAt?: string | Date | null;
    paymentFailed?: boolean;
    priceString?: string | null;
    now?: Date;
  } = {}
): DisplayModel[] {
  const unique = sortEntitlements(dedupeEntitlements(entitlements));
  const longest = unique.length ? accessUntil(unique[0]) : 0;
  return unique.map((entitlement) => {
    const source = subscriptionSource(entitlement.store);
    const redundant = isRedundantRenewal(entitlement, unique);
    return buildDisplayModel(entitlement, {
      // Pause state is a property of the Stripe subscription, so it must not
      // leak onto a store-billed or promotional card sitting alongside it.
      isPaused: source === "stripe" ? opts.isPaused : false,
      pauseResumesAt: opts.pauseResumesAt,
      pauseAccessEndsAt: opts.pauseAccessEndsAt,
      // A Stripe charge that failed is likewise a property of the Stripe
      // subscription, not of a store-billed card sitting next to it.
      paymentFailed: source === "stripe" ? opts.paymentFailed : false,
      now: opts.now,
      // activeWebSubscriptionPrice describes the user's Stripe subscription and
      // nothing else. Applied to whichever entitlement happened to be picked, it
      // would print a Stripe price on a Play Store or promotional card.
      priceString: source === "stripe" ? opts.priceString : null,
      renewalNudge: redundant
        ? redundantRenewalCopy(
            source,
            longOrdinalDate(entitlement.expirationDate),
            longOrdinalDate(longest === Number.POSITIVE_INFINITY ? null : new Date(longest))
          )
        : null,
    });
  });
}

// Every active entitlement RevenueCat knows about, normalised. Replaces
// `Object.values(active)[0]`.
export function entitlementsFromCustomerInfo(customerInfo: any): Entitlement[] {
  const active = customerInfo?.entitlements?.active || {};
  return Object.values(active).map((raw: any) =>
    normalizeEntitlement(raw, toIso(raw?.expirationDate))
  );
}

// --- Loading helpers -------------------------------------------------------
// The RevenueCat SDK hands back Date objects while our GraphQL layer speaks ISO
// strings; normalise to ISO so everything downstream sees one shape.
export function toIso(value: unknown): string | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  return String(value);
}

export function normalizeEntitlement(raw: any, expirationDate: string | null): Entitlement {
  return {
    productIdentifier: raw?.productIdentifier ?? null,
    store: raw?.store ?? null,
    willRenew: raw?.willRenew ?? null,
    originalPurchaseDate: toIso(raw?.originalPurchaseDate),
    expirationDate,
  };
}

// Price and pause state are fetched with Promise.allSettled so one failing
// query can't blank the whole screen — each degrades to its own safe default.
export function priceStringFromResult(result: PromiseSettledResult<any> | null | undefined): string | null {
  if (result?.status !== "fulfilled") return null;
  return result.value?.activeWebSubscriptionPrice?.formattedPrice ?? null;
}

export interface PauseState {
  isPaused: boolean;
  pauseResumesAt: string | null;
  pauseAccessEndsAt: string | null;
  paymentFailed: boolean;
}

export function pauseStateFromResult(result: PromiseSettledResult<any> | null | undefined): PauseState {
  if (result?.status !== "fulfilled") {
    return { isPaused: false, pauseResumesAt: null, pauseAccessEndsAt: null, paymentFailed: false };
  }
  const user = result.value?.user;
  return {
    isPaused: user?.isPaused ?? false,
    pauseResumesAt: toIso(user?.pauseResumesAt),
    pauseAccessEndsAt: toIso(user?.pauseAccessEndsAt),
    paymentFailed: user?.subscriptionPaymentFailed ?? false,
  };
}

export interface CancelReasonOption {
  slug: CancelReasonSlug;
  label: string;
}

export const CANCEL_REASONS: CancelReasonOption[] = [
  { slug: "too_expensive", label: "It's too expensive" },
  { slug: "dont_use_enough", label: "I don't use it enough" },
  { slug: "switching_apps", label: "I'm switching to another app" },
  { slug: "missing_features", label: "Missing features I need" },
  { slug: "not_working", label: "It's not working" },
  { slug: "other", label: "Other" },
];

export interface PauseDurationOption {
  months: number;
  label: string;
}

export const PAUSE_DURATIONS: PauseDurationOption[] = [
  { months: 1, label: "1 Month" },
  { months: 3, label: "3 Months" },
  { months: 6, label: "6 Months" },
];

export const SUBSCRIPTION_LOAD_FAILURE_MESSAGE =
  "We couldn't load your subscription details just now. Please refresh the page or try " +
  "again shortly — this doesn't affect your subscription or your billing.";

export type SubscriptionPanel = "loading" | "error" | "empty" | "manage";

/**
 * Which panel the subscription page shows.
 *
 * Extracted because the page had no error state at all: any failure loading the
 * entitlement fell through to `!display` and rendered the empty state, telling a
 * paying subscriber "You don't have an active subscription!" — the most alarming
 * possible reading of what is usually a transient network blip, on the one screen
 * where someone goes to check they still have what they paid for.
 *
 * `error` deliberately outranks `empty`: when we could not load the subscription
 * we do not know whether one exists, and saying nothing is better than guessing
 * wrong in the direction that panics people.
 */
export function subscriptionPanel(state: {
  loading: boolean;
  loadFailed: boolean;
  hasSubscription: boolean;
}): SubscriptionPanel {
  if (state.loading) return "loading";
  if (state.loadFailed) return "error";
  return state.hasSubscription ? "manage" : "empty";
}
